/**
 * AppState - Centralized reactive state store
 * 
 * Provides a simple pub/sub pattern for application-wide state.
 * Use get/set to access state, subscribe to react to changes.
 */

class AppState {
    #state = {
        patch: { name: 'Untitled', settings: {} },
        selection: { devices: new Set(), connectionId: null, templateId: null },
        auth: { user: null },
        debug: { cableStraightDist: 10 }
    };
    
    #listeners = new Map(); // path -> Set<callback>
    
    /**
     * Get a value from state by dot-notation path
     * @param {string} path - e.g., 'patch.name' or 'selection.devices'
     * @returns {*} The value at the path, or undefined if not found
     */
    get(path) {
        const parts = path.split('.');
        let current = this.#state;
        
        for (const part of parts) {
            if (current === undefined || current === null) {
                return undefined;
            }
            current = current[part];
        }
        
        return current;
    }
    
    /**
     * Set a value in state and notify subscribers
     * @param {string} path - e.g., 'patch.name' or 'debug.cableStraightDist'
     * @param {*} value - The new value
     */
    set(path, value) {
        const parts = path.split('.');
        const lastPart = parts.pop();
        let current = this.#state;
        
        // Navigate to parent
        for (const part of parts) {
            if (current[part] === undefined) {
                current[part] = {};
            }
            current = current[part];
        }
        
        const oldValue = current[lastPart];
        current[lastPart] = value;
        
        // Notify subscribers for exact path and parent paths
        this.#notifyPath(path, value, oldValue);
    }
    
    /**
     * Subscribe to changes at a specific path
     * @param {string} path - Path to watch (e.g., 'patch.name')
     * @param {function} callback - Called with (newValue, oldValue, path)
     * @returns {function} Unsubscribe function
     */
    subscribe(path, callback) {
        if (!this.#listeners.has(path)) {
            this.#listeners.set(path, new Set());
        }
        
        this.#listeners.get(path).add(callback);
        
        // Return unsubscribe function
        return () => {
            const pathListeners = this.#listeners.get(path);
            if (pathListeners) {
                pathListeners.delete(callback);
                if (pathListeners.size === 0) {
                    this.#listeners.delete(path);
                }
            }
        };
    }
    
    /**
     * Notify all relevant subscribers of a change
     * @private
     */
    #notifyPath(path, newValue, oldValue) {
        // Notify exact path subscribers
        const exactListeners = this.#listeners.get(path);
        if (exactListeners) {
            for (const callback of exactListeners) {
                try {
                    callback(newValue, oldValue, path);
                } catch (err) {
                    console.error(`[AppState] Subscriber error for "${path}":`, err);
                }
            }
        }
        
        // Notify parent path subscribers (e.g., 'patch' when 'patch.name' changes)
        const parts = path.split('.');
        for (let i = parts.length - 1; i > 0; i--) {
            const parentPath = parts.slice(0, i).join('.');
            const parentListeners = this.#listeners.get(parentPath);
            if (parentListeners) {
                const parentValue = this.get(parentPath);
                for (const callback of parentListeners) {
                    try {
                        callback(parentValue, undefined, parentPath);
                    } catch (err) {
                        console.error(`[AppState] Subscriber error for "${parentPath}":`, err);
                    }
                }
            }
        }
    }
    
    /**
     * Get the entire state (for debugging)
     * @returns {object} Shallow copy of state
     */
    getAll() {
        return { ...this.#state };
    }
    
    /**
     * Reset state to initial values
     */
    reset() {
        this.#state = {
            patch: { name: 'Untitled', settings: {} },
            selection: { devices: new Set(), connectionId: null, templateId: null },
            auth: { user: null },
            debug: { cableStraightDist: 10 }
        };
        
        // Notify all listeners
        for (const [path, listeners] of this.#listeners) {
            const value = this.get(path);
            for (const callback of listeners) {
                try {
                    callback(value, undefined, path);
                } catch (err) {
                    console.error(`[AppState] Subscriber error on reset for "${path}":`, err);
                }
            }
        }
    }
}

// Singleton export
export const appState = new AppState();

// Also export the class for testing purposes
export { AppState };
